import bindAllOfThis from 'src/utils/BindThisHelper';
import ImageApi from 'src/utils/ImageHelper';
import { PhaserColorWheel } from '../../shared/ColorWheel';
import { TiledGameObject } from 'src/games/shared/phaserHelpers/HandyTypes';
import { EndGameScene } from './EndGameScene';
import { Uniform } from '../objects/Uniform';
import { Color } from 'src/games/shared/ColorValue';
import { ColorChooserPopUpScene } from './ColorChooserPopUpScene';
import TranslateApi from 'src/utils/TranslateHelper';

// Paintbrush helper class to hold drawing attributes
class PaintBrush {
    public thickness: number;
    public color: number;
    public alpha: number;

    // default color yellow, thickness 5
    constructor() {
        this.thickness = 8;
        this.color = 0x223e93;
        this.alpha = 1;
    }
}

export class MainScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    map: Phaser.Tilemaps.Tilemap;

    private paintBrush: PaintBrush = new PaintBrush();
    private lineHistory: Phaser.Geom.Point[];
    private drawing: boolean;
    private finishButton: Phaser.GameObjects.Sprite;

    private uniform: Uniform;
    private mascots: Phaser.GameObjects.Image[];
    private uniformTexture: Phaser.GameObjects.RenderTexture;
    private uniformGraphic: Phaser.GameObjects.Graphics;
    private slebUniformArea: Phaser.Geom.Polygon;
    private humanUniformArea: Phaser.Geom.Polygon;

    private div: HTMLDivElement = document.createElement('div');
    private paintGunCursor: string = 'url(' + require("/src/assets/qci/sprites/cursors/paint_brush.cur") + '), pointer';
    private drawingMode: boolean = false;

    public static CROPPED_IMAGE: string = null;

    humanRadioButton: Phaser.GameObjects.Sprite;
    slebRadioButton: Phaser.GameObjects.Sprite;

    primaryColorChooser: Phaser.GameObjects.Sprite;
    secondaryColorChooser: Phaser.GameObjects.Sprite;
    tertiaryColorChooser: Phaser.GameObjects.Sprite;

    num: number = 0;
    debug: boolean = false;

    private textStyle = { fontFamily: "Russo One, Arial", fontSize: 22, align: "center" };

    // Note: Starts out with sleb type of uniform

    constructor() {
        super({
            key: "MainScene"
        });
    }

    preload(): void {
        this.load.tilemapTiledJSON("map", require("src/assets/qci/maps/uniform_design.json"));
        this.load.image("background", require("src/assets/qci/backgrounds/1a_background.png"));
        this.load.atlas("other", require("src/assets/qci/atlas/gameUniformDesignOther.png"), require("src/assets/qci/atlas/gameUniformDesignOther.json"));
        this.load.atlas("sleb", require("src/assets/qci/atlas/gameUniformDesignSleb.png"), require("src/assets/qci/atlas/gameUniformDesignSleb.json"));
        this.load.atlas("human", require("src/assets/qci/atlas/gameUniformDesignHuman.png"), require("src/assets/qci/atlas/gameUniformDesignHuman.json"));

        PhaserColorWheel.preload(this);
    }

    initialize() {
        bindAllOfThis(this, MainScene.prototype);
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.map = this.make.tilemap({ key: "map" });
        this.mascots = [];
        this.lineHistory = [];
        this.drawing = false;
        this.drawingMode = false;
        document.body.appendChild(this.div);
    }

    create(): void {
        this.initialize();
        this.buildBackgroundContent();
        this.registerEvents();
    }

    buildBackgroundContent() {
        let background = this.add.image(0, 0, "background").setOrigin(0);

        this.buildUniform();
        this.buildButton();
        this.buildColorChoosers();
        this.buildTypeChooser();
        this.createText();
        this.createMascots();
        this.buildColorWheel();
    }

    buildUniform(): void {
        var ob = this.map.getObjectLayer("Scene").objects.find((ob) => ob.name === "uniform") as TiledGameObject;

        this.uniform = new Uniform(this, ob.x + ob.width / 2, ob.y + ob.height / 2, "other");

        this.createUniformAreas();

        this.uniformTexture = this.add.renderTexture(0, 0, this.gameWidth, this.gameHeight);
        this.uniformTexture.setDepth(6);

        this.uniformGraphic = this.add.graphics();
        this.uniformGraphic.setDepth(6);
    }

    buildColorChoosers() {
        var colors = this.map.getObjectLayer("Scene").objects.filter((ob) => ob.type === "color");
        colors.forEach((ob: TiledGameObject) => {
            if (ob.name === "primary") {
                this.primaryColorChooser = this.add.sprite(ob.x, ob.y, "other", "color" + this.uniform.primaryColor); // Color picker sprite
                this.primaryColorChooser.setOrigin(0);
                this.primaryColorChooser.setInteractive({ useHandCursor: true });
                this.primaryColorChooser.setDisplaySize(80, 40);
                this.primaryColorChooser.on("pointerdown", () => {
                    this.scene.setActive(false);
                    this.scene.add("ColorChooserScene", new ColorChooserPopUpScene("primary", this.num++), true);
                });
            }
            else if (ob.name === "secondary") {
                this.secondaryColorChooser = this.add.sprite(ob.x, ob.y, "other", "color" + this.uniform.secondaryColor); // Color picker sprite
                this.secondaryColorChooser.setOrigin(0);
                this.secondaryColorChooser.setInteractive({ useHandCursor: true });
                this.secondaryColorChooser.setDisplaySize(80, 40);
                this.secondaryColorChooser.on("pointerdown", () => {
                    this.scene.setActive(false);
                    this.scene.add("ColorChooserScene", new ColorChooserPopUpScene("secondary", this.num++), true);
                });
            }
            else if (ob.name === "tertiary") {
                this.tertiaryColorChooser = this.add.sprite(ob.x, ob.y, "other", "color" + this.uniform.tertiaryColor); // Color picker sprite
                this.tertiaryColorChooser.setOrigin(0);
                this.tertiaryColorChooser.setInteractive({ useHandCursor: true });
                this.tertiaryColorChooser.setDisplaySize(80, 40);
                this.tertiaryColorChooser.on("pointerdown", () => {
                    this.scene.setActive(false);
                    this.scene.add("ColorChooserScene", new ColorChooserPopUpScene("tertiary", this.num++), true);
                });
            }
        });
    }


    buildButton(): void {
        var ob = this.map.getObjectLayer("Scene").objects.find((ob) => ob.name === "button") as TiledGameObject;
        this.finishButton = this.add.sprite(ob.x, ob.y, "other", "button1");
        this.finishButton.setOrigin(0);
        this.finishButton.setInteractive({ useHandCursor: true });

        this.finishButton.on('pointerover', () => {
            this.finishButton.setFrame("button2");
        });

        this.finishButton.on('pointerout', () => {
            this.finishButton.setFrame("button1");
        });

        this.finishButton.on("pointerdown", () => {
            this.finishButton.setFrame("button2");
            this.drawingMode = false;
            // Screenshot for later
            this.registry.parent.renderer.snapshot((image: HTMLImageElement) => {
                image.onload = () => {
                    var croppedImage = ImageApi.CropImageToDataURI(image, 0, 0, this.sys.canvas.width - 300, this.sys.canvas.height);
                    console.warn("Screenshot!", croppedImage); // JB - Remove this console.warn
                    this.endGame(croppedImage);
                };
            });
        });
    }

    buildTypeChooser() {
        var ob;
        // radio1 = unselected, radio2 = selected
        ob = this.map.getObjectLayer("Scene").objects.find((ob) => ob.name === "human") as TiledGameObject;
        this.humanRadioButton = this.add.sprite(ob.x, ob.y, "other", "radio1");
        this.humanRadioButton.setInteractive({ useHandCursor: true });
        this.humanRadioButton.on("pointerdown", () => {
            this.humanRadioButton.setFrame("radio2");
            this.slebRadioButton.setFrame("radio1");
            this.uniform.type = "human";
            this.uniform.refresh();
        });

        ob = this.map.getObjectLayer("Scene").objects.find((ob) => ob.name === "sleb") as TiledGameObject;
        this.slebRadioButton = this.add.sprite(ob.x, ob.y, "other", "radio2");
        this.slebRadioButton.setInteractive({ useHandCursor: true });
        this.slebRadioButton.on("pointerdown", () => {
            this.slebRadioButton.setFrame("radio2");
            this.humanRadioButton.setFrame("radio1");
            this.uniform.type = "sleb";
            this.uniform.refresh();
        });
    }

    createUniformAreas() {
        var ob = this.map.getObjectLayer("Scene").objects.find((ob) => ob.name === "slebPoly") as TiledGameObject;
        var points = ob["polygon"];
        points.forEach((p) => {
            p.x += ob.x;
            p.y += ob.y;
        });
        this.slebUniformArea = new Phaser.Geom.Polygon(points);

        ob = this.map.getObjectLayer("Scene").objects.find((ob) => ob.name === "humanPoly") as TiledGameObject;
        points = ob["polygon"];
        points.forEach((p) => {
            p.x += ob.x;
            p.y += ob.y;
        });
        this.humanUniformArea = new Phaser.Geom.Polygon(points);

        if(this.debug) {
            this.drawPolygonUniformArea(this.slebUniformArea);
            this.drawPolygonUniformArea(this.humanUniformArea);
        }
    }

    buildColorWheel(): void {
        var ob = this.map.getObjectLayer("Scene").objects.find((ob) => ob.name === "colorwheel") as TiledGameObject;
        var colorWheel = new PhaserColorWheel(this, ob.x, ob.y, 175, 175);
        colorWheel.on("colorChange", (color: Phaser.Display.Color) => {
            this.drawingMode = true;
            this.paintBrush.color = color.color;
            this.refreshPaintBrush();
            this.input.setDefaultCursor(this.paintGunCursor);
        });
        colorWheel.setDepth(2);
    }

    createText() {
        this.map.getObjectLayer("Text").objects.forEach((ob: TiledGameObject) => {
            var key = ob.name === "done" ? "common.done" : "UniformDesign.Phaser." + ob.properties["key"];
            var text = this.add.text(ob.x, ob.y, TranslateApi.translate(key), this.textStyle);
            text.setWordWrapWidth(200, true);

            if(ob.name === "done") {
                text.setColor("#000000");
            }
        });
    }

    createMascots() {
        this.mascots = [];
        this.map.getObjectLayer("Mascots").objects.forEach((ob: TiledGameObject) => {
            this.createMascot(ob);
        });
    }

    createMascot(ob: TiledGameObject) {
        var mascot = this.add.image(ob.x + ob.width / 2, ob.y + ob.height / 2, "other", ob.name);
        mascot.setInteractive({ useHandCursor: true });
        mascot.name = ob.name;
        mascot.on("pointerdown", () => {
            this.drawingMode = false;
            this.drawing = false;
            if (mascot.name === "remove") {
                if (this.uniform.mascot != null) {
                    this.uniform.mascot.destroy();
                    this.uniform.mascot = null;
                    this.uniform.mascotKey = null;
                }
            }
            else {
                this.uniform.mascotKey = ob.name;
            }
            this.uniform.refresh();
        });
        this.mascots.push(mascot);
    }

    refreshPaintBrush() {
        this.uniformGraphic.lineStyle(this.paintBrush.thickness, this.paintBrush.color, this.paintBrush.alpha);
    }

    registerEvents(): void {
        this.input.on('pointerdown', this.pointerDown);
        this.input.on('pointerup', this.pointerUp);
        this.input.on('pointermove', this.pointerMove);
    }

    canDraw(pointer: Phaser.Input.Pointer) {
        var area = this.uniform.type === "sleb" ? this.slebUniformArea : this.humanUniformArea;
        if (area.contains(pointer.x, pointer.y)) {
            return true;
        }

        return false;
    }

    pointerDown(pointer: Phaser.Input.Pointer): void {
        if (this.drawingMode && this.canDraw(pointer)) {
            this.drawing = true;
        }

        this.lineHistory = [];
    }

    pointerUp(pointer: Phaser.Input.Pointer): void {
        this.drawing = false;
        this.uniformGraphic.generateTexture("background-drawn", this.gameWidth, this.gameHeight);
        this.uniformGraphic.clear();

        let tempImage: Phaser.GameObjects.Image = this.add.image(0, 0, "background-drawn");
        this.uniformTexture.save();
        this.uniformTexture.draw(tempImage.texture, tempImage.frame, 0, 0);
        this.uniformTexture.restore();

        tempImage.destroy();
    }

    pointerMove(pointer: Phaser.Input.Pointer): void {
        if (this.drawing && this.canDraw(pointer)) {
            this.uniformGraphic.clear();
            this.refreshPaintBrush();
            this.lineHistory.push(new Phaser.Geom.Point(pointer.x, pointer.y));
            // Do the drawing
            this.uniformGraphic.strokePoints(this.lineHistory);
        }

        if (this.drawingMode && this.canDraw(pointer)) {
            this.input.setDefaultCursor(this.paintGunCursor);
        }
        else {
            this.input.setDefaultCursor("pointer");
        }
    }

    endGame(image: string): void {
        MainScene.CROPPED_IMAGE = image;

        this.input.setDefaultCursor("default");
        this.input.removeAllListeners();
        this.scene.add("EndGameScene", EndGameScene, true);
        this.scene.remove(this);
    }

    drawPolygonUniformArea(polygon) {
        // Draw polygon and set it up
        var graphics = this.add.graphics();
        graphics.lineStyle(2, 0x000000);
        graphics.beginPath();
        graphics.moveTo(polygon.points[0].x, polygon.points[0].y);
        for (var i = 1; i < polygon.points.length; i++) {
            graphics.lineTo(polygon.points[i].x, polygon.points[i].y);
        }
        graphics.closePath();
        graphics.strokePath();
    }

    changeColor(colorType, color: number) {
        if (colorType === "primary") {
            this.uniform.primaryColor = color;
            this.primaryColorChooser.setFrame("color" + color);
        }
        else if (colorType === "secondary") {
            this.uniform.secondaryColor = color;
            this.secondaryColorChooser.setFrame("color" + color);
        }
        else if (colorType === "tertiary") {
            this.uniform.tertiaryColor = color;
            this.tertiaryColorChooser.setFrame("color" + color);
        }
        this.uniform.refresh();
        this.scene.setActive(true);
    }
}



// WEBPACK FOOTER //
// ./src/games/UniformDesignGame/scenes/MainScene.ts